/**
 * A HAND-BUILT GUARANTEED INCOME ABOVE THE CLIENT'S CEILING (owner, 21 Oct 2026 — the guaranteed income review
 * M3). A plan built by hand may put in more than the share the client gave (or the suggested quarter): it is
 * their plan, so this is a caution beside the amount, never a block.
 *
 *   "This puts N% of what {name} has into a guaranteed income. We suggest no more than 25%, so most of the
 *    money stays within reach."
 *
 * The suggestion is the client's own ceiling where they answered (annuityCeilingOf), else the suggested 25%.
 * White with an amber rail (no pastel infill: noPastelPanels.test.ts).
 */
import React from 'react';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { annuityCeilingOf, annuityCeilingAmount } from '../../../../shared/src/calculation/annuityPreference.ts';

/** The caution's words, or null when the amount is within the ceiling. */
export function annuityCeilingCautionWords(factFind: DraftFactFind | null | undefined, amount: number, investable: number, personName?: string | null, moduleSharePct?: number | null): string | null {
    const a = Math.max(0, Number(amount) || 0);
    const inv = Math.max(0, Number(investable) || 0);
    if (!(a > 0) || !(inv > 0)) return null;
    const ceiling = annuityCeilingOf(factFind as never, moduleSharePct);
    if (a <= annuityCeilingAmount(ceiling, inv) + 1) return null;
    const v = householdVoice(factFind);
    const who = personName || (v.you ? 'you' : v.names);
    const has = personName ? 'has' : v.have;
    const pct = Math.round((a / inv) * 100);
    const suggest = ceiling.amount != null ? formatCurrency(Math.round(ceiling.amount)) : `${ceiling.pct}%`;
    return `This puts ${pct}% of what ${who} ${has} into a guaranteed income. We suggest no more than ${suggest}, so most of the money stays within reach.`;
}

export const AnnuityCeilingCaution: React.FC<{
    factFind: DraftFactFind | null | undefined;
    amount: number;
    /** The money that could be invested (what the sale or the move frees). */
    investable: number;
    personName?: string | null;
    moduleSharePct?: number | null;
    testId?: string;
}> = ({ factFind, amount, investable, personName, moduleSharePct, testId = 'annuity-ceiling-caution' }) => {
    const words = annuityCeilingCautionWords(factFind, amount, investable, personName, moduleSharePct);
    if (!words) return null;
    return (
        <p className="mt-2 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2 text-[12.5px] leading-relaxed text-slate-700" data-testid={testId}>
            {words}
        </p>
    );
};

export default AnnuityCeilingCaution;
